import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toFileInputDtoList, toFileListFormValues } from '../file/utils';
import { queryMe } from '../me/api';
import { GenerateImageList } from './components/generate-image-list';
import {
  ImageGenerationForm,
  useImageGenerationForm,
  useImageGenerationSubmit,
} from './components/image-generation-form';
import { DEFAULT_IMAGE_MODEL_CODE } from './image-llm';
import {
  useReadImageGenerationPageData,
  useSetImageGenerationPageData,
} from './store';

export const ImageGenerationPage = () => {
  const { data } = useQuery(queryMe);
  return <ImageGenerationPageContent key={data.name} />;
};

const ImageGenerationPageContent = () => {
  const readPageData = useReadImageGenerationPageData();
  const setPageData = useSetImageGenerationPageData();
  const pageData = readPageData();
  const form = useImageGenerationForm({
    name: pageData.name ?? '',
    prompt: pageData.prompt ?? '',
    file_list: toFileListFormValues(pageData.images ?? []),
    aspect_ratio: pageData.aspect_ratio ?? '9:16',
    model: pageData.model ?? DEFAULT_IMAGE_MODEL_CODE,
    quantity: pageData.quantity ?? 1,
  });
  const generation = useImageGenerationSubmit(form);
  const { subscribe } = form;

  useEffect(
    () =>
      subscribe({
        formState: { values: true },
        callback: ({ values }) => {
          setPageData({
            name: values.name,
            prompt: values.prompt,
            images: toFileInputDtoList(values.file_list),
            aspect_ratio: values.aspect_ratio,
            model: values.model,
            quantity: values.quantity,
          });
        },
      }),
    [subscribe, setPageData],
  );

  return (
    <div className="flex w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:h-full sm:flex-row sm:divide-x sm:divide-y-0">
      <div className="card animate-fade-in sm:overflow-auto xl:w-[640px]">
        <ImageGenerationForm
          form={form}
          isPending={generation.isPending}
          onSubmit={generation.submit}
          onReset={() => {
            form.reset({
              name: '',
              prompt: '',
              file_list: [],
              aspect_ratio: '9:16',
              model: DEFAULT_IMAGE_MODEL_CODE,
              quantity: 1,
            });
          }}
        />
      </div>
      <div className="card flex-auto animate-slide-up space-y-6 sm:min-h-40 sm:flex-1 sm:overflow-auto">
        <GenerateImageList
          onEdit={(task) => {
            form.setValues((values) => ({
              name: task.generate_data?.name ?? values.name,
              prompt: task.generate_data?.prompt ?? values.prompt,
              aspect_ratio: task.generate_data?.aspect_ratio ?? '1:1',
              file_list: toFileListFormValues(task.result?.image_urls ?? []),
              model: values.model,
              quantity: values.quantity ?? 1,
            }));
          }}
        />
      </div>
    </div>
  );
};
